import React, { useEffect, useState, useContext } from "react";
import { useParams, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { GlobalContext } from "../../../Context/GlobalContext";
import { PlusCircle } from "lucide-react";
import { FaPencilAlt } from "react-icons/fa";
import { IoTrashBin } from "react-icons/io5";
import { LoaderSkeleton } from "../../Clients/WhatsAppProfileModal";
import useCatalogue from "../../Services/Catalogue/useCatalogue";

const EditIngredients = () => {
  const { itemId } = useParams();
  const location = useLocation();
  const { addRecipeIngredient, getRecipeIngredient } = useCatalogue();
  const itemName = location.state?.name || "Unnamed Item";
  const itemPhoto = location.state?.photo || "";
  const { selectedClient } = useContext(GlobalContext);
  const userID = selectedClient?.code || "";
  const [loading, setLoading] = useState(true);
  const [ingredients, setIngredients] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);

  const [newIngredients, setNewIngredients] = useState([
    { ingredientName: "", quantity: "", unit: "" },
  ]);

  const addIngredientRow = () => {
    setNewIngredients([
      ...newIngredients,
      { ingredientName: "", quantity: "", unit: "" },
    ]);
  };

  const removeIngredientRow = (index) => {
    setNewIngredients(newIngredients.filter((_, i) => i !== index));
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...newIngredients];
    updated[index][field] = value;
    setNewIngredients(updated);
  };

  const handleSubmitAll = async () => {
    const hasEmpty = newIngredients.some(
      (ing) => !ing.ingredientName || !ing.quantity || !ing.unit,
    );

    if (hasEmpty) {
      toast.error("Please fill all ingredient fields before submitting.");
      return;
    }

    const payload = {
      userid: userID,
      itemId,
      ingredients: newIngredients,
    };

    // ✅ 1. ADD API
    const res = await addRecipeIngredient(payload);

    if (res.success) {
      toast.success(res.message);

      // ✅ 2. INSTANT GET API CALL
      const getRes = await getRecipeIngredient({
        userid: userID,
        itemId,
      });

      if (getRes.success) {
        setIngredients(getRes.data); // 🔥 TABLE UPDATED HERE
        setShowAddForm(false); // hide form → show table
      } else {
        setIngredients([]);
      }

      // ✅ 3. RESET FORM
      setNewIngredients([{ ingredientName: "", quantity: "", unit: "" }]);
    } else {
      toast.error(res.message);
    }
  };

  const fetchIngredients = async () => {
    setLoading(true);

    const res = await getRecipeIngredient({
      userid: userID,
      itemId,
    });

    if (res.success) {
      setIngredients(res.data);
      setShowAddForm(res.data.length === 0);
    } else {
      setIngredients([]);
      setShowAddForm(true);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchIngredients();
  }, []);

  return (
    <div className="p-4" style={{ fontFamily: "Nunito, sans-serif" }}>
      <ToastContainer />
      <div className="d-flex align-items-center mb-4">
        <div
        className="mb-4"
          style={{
            width: "400px",
            height: "400px",
            borderRadius: "10px",
            overflow: "hidden",
            background: '#gggg'
          }}
        >
          <img
            src={itemPhoto}
            alt={itemName}
            className="w-100 h-100 object-fit-cover"
          />
        </div>
        <div className="ms-0 border rounded p-2 bg-light">
          <h4 className="fw-bold">{itemName}</h4>
          <small className="text-muted">Item ID: {itemId}</small>
        </div>
      </div>

      {loading ? (
        <div className="text-center p-5">
           <LoaderSkeleton />
        </div>
      ) : (
        <>
          {ingredients.length > 0 ? (
            <>
              <h5 className="fw-semibold mb-3">Existing Ingredients</h5>
              <div className="border rounded-md overflow-hidden">
                {/* Header */}
                <div className="grid grid-cols-12 bg-gray-100 border-b text-sm font-semibold text-gray-700">
                  <div className="col-span-1 px-3 py-2 border-r">#</div>
                  <div className="col-span-4 px-3 py-2 border-r">
                    Ingredient
                  </div>
                  <div className="col-span-2 px-3 py-2 border-r">Quantity</div>
                  <div className="col-span-2 px-3 py-2 border-r">Unit</div>
                  <div className="col-span-3 px-3 py-2">Actions</div>
                </div>

                {/* Rows */}
                {ingredients.map((ing, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 text-sm border-b last:border-b-0 items-center"
                  >
                    <div className="col-span-1 px-3 py-2 border-r">
                      {idx + 1}
                    </div>

                    <div className="col-span-4 px-3 py-2 border-r">
                      {ing.ingredient_name}
                    </div>

                    <div className="col-span-2 px-3 py-2 border-r">
                      {ing.quantity}
                    </div>

                    <div className="col-span-2 px-3 py-2 border-r">
                      {ing.unit}
                    </div>

                    <div className="col-span-3 px-3 py-2 flex gap-2">
                      <button className="flex items-center gap-1 text-blue-600 border border-blue-500 px-2 py-1 rounded text-xs hover:bg-blue-50">
                        <FaPencilAlt />Edit
                      </button>

                      <button className="flex items-center gap-1 text-red-600 border border-red-500 px-2 py-1 rounded text-xs hover:bg-red-50">
                        <IoTrashBin />Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {!showAddForm && (
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-md shadow-sm transition"
                  >
                    <PlusCircle size={16} />
                    Add More
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-4 border rounded bg-light">
              <p className="mb-3">No ingredients found for this item.</p>
              <p className="text-muted">
                Please start by adding new ingredients below.
              </p>
            </div>
          )}
        </>
      )}

      {showAddForm && (
        <div className="mt-8">
          <hr className="mb-4" />

          <h5 className="font-semibold text-lg mb-4">Add Ingredients</h5>

          {/* Header Row */}
          <div className="grid grid-cols-12 gap-4 mb-2 text-sm font-medium text-gray-600">
            <div className="col-span-4">Ingredient</div>
            <div className="col-span-3">Quantity</div>
            <div className="col-span-3">Unit</div>
            <div className="col-span-2 text-right"></div>
          </div>

          {/* Input Rows */}
          {newIngredients.map((ing, index) => (
            <div
              key={index}
              className="grid grid-cols-12 gap-4 items-center mb-3"
            >
              {/* Ingredient */}
              <div className="col-span-4">
                <input
                  type="text"
                  value={ing.ingredientName}
                  placeholder="e.g. Tomato"
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) =>
                    handleInputChange(index, "ingredientName", e.target.value)
                  }
                />
              </div>

              {/* Quantity */}
              <div className="col-span-3">
                <input
                  type="text"
                  value={ing.quantity}
                  placeholder="e.g. 200"
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) =>
                    handleInputChange(index, "quantity", e.target.value)
                  }
                />
              </div>

              {/* Unit */}
              <div className="col-span-3">
                <input
                  type="text"
                  value={ing.unit}
                  placeholder="e.g. gm/ml"
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) =>
                    handleInputChange(index, "unit", e.target.value)
                  }
                />
              </div>

              {/* Remove Button */}
              <div className="col-span-2 text-right">
                <button
                  onClick={() => removeIngredientRow(index)}
                  disabled={newIngredients.length === 1}
                  className="bg-red-400 hover:bg-red-500 text-white text-sm px-3 py-2 rounded-md disabled:opacity-50"
                >
                  ⛔ Remove
                </button>
              </div>
            </div>
          ))}

          {/* Buttons */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={addIngredientRow}
              className="border border-blue-500 text-blue-600 px-4 py-2 rounded-md text-sm hover:bg-blue-50"
            >
              + Add Another Row
            </button>

            <button
              onClick={handleSubmitAll}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm"
            >
              Submit Ingredients
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditIngredients;
